// "Three reasons" showcase: a numbered list on the left drives a product screenshot on the
// right. It auto-advances (paused on hover/focus and under reduced motion); click to pick one.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import "../../css/WhyReasons.css";

const DURATION = 7000;

export default function WhyReasons({ reasons }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    setReduced(Boolean(mq && mq.matches));
  }, []);

  useEffect(() => {
    if (paused || reduced) return undefined;
    const t = setTimeout(() => setActive(a => (a + 1) % reasons.length), DURATION);
    return () => clearTimeout(t);
  }, [active, paused, reduced, reasons.length]);

  const current = reasons[active];

  return (
    <div
      className={`why-reasons why-reasons--${current.tone}${paused || reduced ? " is-paused" : ""}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{ "--dur": `${DURATION}ms` }}
    >
      <div className="why-reasons__copy">
        <span className="why-reasons__eyebrow">What sets us apart</span>
        <h2 className="why-reasons__title">
          Three reasons teams <span>choose Froth TestOps</span>
        </h2>

        <ol className="why-reasons__list">
          {reasons.map((r, i) => {
            const on = i === active;
            return (
              <li key={r.title} className={`why-reason${on ? " is-active" : ""}`}>
                <button
                  type="button"
                  className="why-reason__head"
                  aria-expanded={on}
                  onClick={() => setActive(i)}
                >
                  <span className="why-reason__num">0{i + 1}</span>
                  <span className="why-reason__title">{r.title}</span>
                </button>
                <span className="why-reason__bar" aria-hidden="true">
                  {on && <i key={active} />}
                </span>
                <div className="why-reason__body" hidden={!on}>
                  <p>{r.text}</p>
                  <Link to={r.link.to} className="why-reason__link">
                    {r.link.label} <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="why-reasons__stage">
        <span className="why-reasons__glow" aria-hidden="true" />
        <div className="why-reasons__frame">
          <div className="why-reasons__bar" aria-hidden="true">
            <i />
            <i />
            <i />
            <span>{current.tag}</span>
          </div>
          {/* The frame takes the active screenshot's own ratio, so nothing is cropped. */}
          <div className="why-reasons__shots" style={{ aspectRatio: `${current.size[0]} / ${current.size[1]}` }}>
            {reasons.map((r, i) => (
              <img
                key={r.title}
                src={r.shot}
                width={r.size[0]}
                height={r.size[1]}
                alt={i === active ? r.alt : ""}
                className={i === active ? "is-active" : ""}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </div>
        <span className="why-reasons__chip" key={current.chip}>
          {current.ai && <FrothAiIcon size={15} />}
          {current.chip}
        </span>
      </div>
    </div>
  );
}
